import { createSlice } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";
import { normalizeMessage, normalizeMessages, normalizeRoom, normalizeRooms } from "@/utils/common";

const initialState = {
    rooms: [],
    selectedRoom: {},
    unreadCount: 0,
    loadRoomMessageStatus: {},
};

const persistConfig = {
    key: "rooms",
    storage,
    whitelist: ["rooms", "selectedRoom", "unreadCount", "loadRoomMessageStatus"], // place to select which state you want to persist
};

const findRoomIndex = (rooms, roomId) => {
    if (!rooms) return -1;

    for (let i = 0; i < rooms.length; i++) {
        if (rooms[i].id === roomId) return i;
    }

    return -1;
};

const updateRoomUser = (room, userId, roomUser) => {
    if (room.room_users) {
        for (let i = 0; i < room.room_users.length; i++) {
            if (room.room_users[i].user_id === userId) {
                room.room_users[i] = roomUser;
                return room;
            }
        }

        room.room_users.push(roomUser);
        return room;
    }

    room.room_users = [roomUser];
    return room;
};

const roomSlice = createSlice({
    name: "room",
    initialState,
    reducers: {
        setRoomList: (state, action) => {
            state.rooms = normalizeRooms(action.payload);
        },
        createRoom: (state, action) => {
            state.rooms.push(normalizeRoom(action.payload));
        },
        selectRoom: (state, action) => {
            state.selectedRoom = normalizeRoom(action.payload) || {};
        },
        selectRoomClear: (state) => {
            state.selectedRoom = {};
        },
        updateLatestMessage: (state, action) => {
            const payload = action.payload || {};
            const messages = normalizeMessages(Array.isArray(payload) ? payload : payload.messages);
            if (!messages || messages.length === 0) return;

            const latestMessage = messages[0];
            const latestRoomId = Number(latestMessage.room_id);
            const latestUserId = Number(latestMessage.user_id);
            const currentUserId = Number(payload.currentUserId);
            const roomIndex = state.rooms.findIndex((room) => Number(room.id) === latestRoomId);

            if (roomIndex === -1) return;

            const room = state.rooms[roomIndex];
            const currentLast = room.last_message;
            const shouldUpdateLastMessage =
                !currentLast ||
                Number(latestMessage.created_at) >= Number(currentLast.created_at);

            if (shouldUpdateLastMessage) {
                room.last_message = latestMessage;
            }

            if (currentLast?.id === latestMessage.id) {
                room.last_message = {
                    ...currentLast,
                    ...latestMessage
                };
            }

            if (state.selectedRoom && Number(room.id) !== Number(state.selectedRoom.id)) {
                if (latestUserId !== currentUserId) {
                    room.unread_count = (room.unread_count || 0) + 1;
                }
            }
        },
        updateRoomLastMessage: (state, action) => {
            const { room_id, last_message } = action.payload || {};
            const roomIndex = findRoomIndex(state.rooms, room_id);

            if (roomIndex === -1) return;

            const normalizedLastMessage = normalizeMessage(last_message);

            state.rooms[roomIndex].last_message = normalizedLastMessage;

            if (state.selectedRoom?.id === room_id) {
                state.selectedRoom = {
                    ...state.selectedRoom,
                    last_message: normalizedLastMessage,
                };
            }
        },
        resetUnreadMessageCount: (state, action) => {
            const data = action.payload || {};
            const roomIndex = state.rooms.findIndex((room) => room.id === data.room_id);

            if (roomIndex === -1) return;
            if (state.rooms[roomIndex].unread_count === data.unread_count) return;

            const unreadCount = data.atBottom ? 0 : (state.rooms[roomIndex].unread_count || 0) + 1;
            state.rooms[roomIndex].unread_count = unreadCount;

            if (state.selectedRoom?.id === data.room_id) {
                state.selectedRoom = { ...state.selectedRoom, unread_count: unreadCount };
            }
        },
        clearUnreadMessageCount: (state) => {
            state.unreadCount = 0;
        },
        clearRooms: (state) => {
            state.rooms = [];
        },
        calculateUnseenMessageCount: (state) => {
            state.unreadCount = state.rooms.reduce((count, room) => count + (room.unread_count ? 1 : 0), 0);
        },
        insertRoomUser: (state, action) => {
            const roomUser = action.payload;
            const roomIndex = findRoomIndex(state.rooms, roomUser?.room_id);

            if (roomIndex === -1) return;

            const room = updateRoomUser(state.rooms[roomIndex], roomUser.user_id, roomUser);

            if (room.id === state.selectedRoom?.id) {
                state.selectedRoom = room;
            }
        },
        setRoomInitialized: (state, action) => {
            const data = action.payload || {};
            if (!data.room_id) return;

            state.loadRoomMessageStatus[data.room_id] = {
                ...(state.loadRoomMessageStatus[data.room_id] || {}),
                initialized: data.status,
            };
        },
        clearRoomInitialized: (state) => {
            state.loadRoomMessageStatus = {};
        },
    },
});

export const {
    setRoomList,
    createRoom,
    selectRoom,
    selectRoomClear,
    updateLatestMessage,
    updateRoomLastMessage,
    resetUnreadMessageCount,
    clearUnreadMessageCount,
    clearRooms,
    calculateUnseenMessageCount,
    insertRoomUser,
    setRoomInitialized,
    clearRoomInitialized,
} = roomSlice.actions;

export default persistReducer(persistConfig, roomSlice.reducer);
